import * as Sentry from "@sentry/react";
type Extras = Record<string, unknown>;
import makeDebug from "debug";
import type { User } from "oidc-client-ts";
import type { ErrorInfo } from "react";
import { getEnvironment } from "./environment";
import { isChunkError } from "./lazyWithRetry";

const debug = makeDebug("sentry");
let dsn = "";

const environment = getEnvironment();

if (environment === "prod") {
	dsn =
		"https://55eeb05824331e28b32f5cfc1fe3f100@o4507270694764544.ingest.de.sentry.io/4507270705250384";
	debug(
		`Sentry enabled. DSN: ${dsn}. App Version: ${
			import.meta.env.VITE_VERSION
		}. Environment: ${environment}`,
	);
} else {
	debug("Not using sentry because of development mode");
}

Sentry.init({
	normalizeDepth: 20,
	dsn,
	tunnel: dsn ? "https://snt.kultify.com/tunnel" : undefined,
	environment,
	release: import.meta.env.VITE_VERSION || "development",
	ignoreErrors: [
		"ResizeObserver loop limit exceeded",
		"AbortError",
		"The user aborted a request",
	],
	beforeSend(event, hint) {
		const error = hint?.originalException;
		// Drop cancelled/aborted requests (e.g. component unmount, navigation)
		if (error instanceof DOMException && error.name === "AbortError") {
			return null;
		}
		return event;
	},
});

export function reportError(
	err: Error,
	showReportDialog = false,
	details?: unknown,
) {
	try {
		if (import.meta.env.PROD) {
			console.error("report error", err);
			Sentry.captureException(err, (scope) => {
				scope.setExtras(details as Extras);
				return scope;
			});
			if (showReportDialog) {
				Sentry.showReportDialog();
			}
		} else {
			console.warn("Not reporting following error:");
			console.error(err);
		}
	} catch (err) {
		console.error("error while reporting error", err);
	}
}

export function handleReactError(error: unknown, info: ErrorInfo) {
	if (isChunkError(error)) {
		console.warn("New version available.");
		return;
	}
	console.error("react error", error, info.componentStack);
	Sentry.withScope((scope) => {
		scope.setExtra("componentStack", info.componentStack);
		Sentry.captureException(error);
	});
}

export function handleSWRError(error: Error, key: string) {
	console.error("swr error", error, key);
	Sentry.withScope((scope) => {
		scope.setExtra("key", key);
		Sentry.captureException(error);
	});
}

export function identifyUser(user: User) {
	Sentry.getCurrentScope().setUser({
		id: user.profile.sub,
		// disabled for privacy
		//  email: user.profile.email
	});
}
